import React from 'react';
import { TaxCurrencyInput, TaxPercentageInput } from './components';
import { useTaxAnalysisInputs, TaxAnalysisInputValues, FIELD_KEYS, FIELD_LABEL_KEYS } from '../../hooks';
import { formatMessage } from '@/utils/i18n';

interface TaxAnalysisInputsContainerProps {
  readOnly?: boolean;
  taxInputs: ReturnType<typeof useTaxAnalysisInputs>;
}

export type { TaxAnalysisInputValues };

const INPUT_TYPE = {
  CURRENCY: 'currency',
  PERCENTAGE: 'percentage',
} as const;

type InputType = (typeof INPUT_TYPE)[keyof typeof INPUT_TYPE];

interface InputConfig {
  key: keyof TaxAnalysisInputValues;
  type: InputType;
}

const INPUT_CONFIGS: InputConfig[] = [
  { key: FIELD_KEYS.SHORT_TERM_YTD_GAIN, type: INPUT_TYPE.CURRENCY },
  { key: FIELD_KEYS.SHORT_TERM_YTD_LOSS, type: INPUT_TYPE.CURRENCY },
  { key: FIELD_KEYS.LONG_TERM_YTD_GAIN, type: INPUT_TYPE.CURRENCY },
  { key: FIELD_KEYS.LONG_TERM_YTD_LOSS, type: INPUT_TYPE.CURRENCY },
  { key: FIELD_KEYS.SHORT_TERM_CARRIED_FORWARD_LOSS, type: INPUT_TYPE.CURRENCY },
  { key: FIELD_KEYS.LONG_TERM_CARRIED_FORWARD_LOSS, type: INPUT_TYPE.CURRENCY },
  { key: FIELD_KEYS.SHORT_TERM_CAPITAL_GAINS_RATE, type: INPUT_TYPE.PERCENTAGE },
  { key: FIELD_KEYS.LONG_TERM_CAPITAL_GAINS_RATE, type: INPUT_TYPE.PERCENTAGE },
];

const TaxAnalysisInputsContainer: React.FC<TaxAnalysisInputsContainerProps> = ({ readOnly = false, taxInputs }) => {
  const {
    values,
    setShortTermYTDGain,
    setShortTermYTDLoss,
    setLongTermYTDGain,
    setLongTermYTDLoss,
    setShortTermCarriedForwardLoss,
    setLongTermCarriedForwardLoss,
    setShortTermCapitalGainsRate,
    setLongTermCapitalGainsRate,
  } = taxInputs;

  const setterMap: Record<keyof TaxAnalysisInputValues, (value?: number) => void> = {
    [FIELD_KEYS.SHORT_TERM_YTD_GAIN]: setShortTermYTDGain,
    [FIELD_KEYS.SHORT_TERM_YTD_LOSS]: setShortTermYTDLoss,
    [FIELD_KEYS.LONG_TERM_YTD_GAIN]: setLongTermYTDGain,
    [FIELD_KEYS.LONG_TERM_YTD_LOSS]: setLongTermYTDLoss,
    [FIELD_KEYS.SHORT_TERM_CARRIED_FORWARD_LOSS]: setShortTermCarriedForwardLoss,
    [FIELD_KEYS.LONG_TERM_CARRIED_FORWARD_LOSS]: setLongTermCarriedForwardLoss,
    [FIELD_KEYS.SHORT_TERM_CAPITAL_GAINS_RATE]: setShortTermCapitalGainsRate,
    [FIELD_KEYS.LONG_TERM_CAPITAL_GAINS_RATE]: setLongTermCapitalGainsRate,
  };

  return (
    <div className="tax-inputs-container">
      {INPUT_CONFIGS.map(({ key, type }) => {
        const InputComponent = type === INPUT_TYPE.CURRENCY ? TaxCurrencyInput : TaxPercentageInput;

        return (
          <InputComponent key={key} id={key} label={formatMessage(FIELD_LABEL_KEYS[key])} value={values[key]} onChange={setterMap[key]} disabled={readOnly} />
        );
      })}
    </div>
  );
};

export default TaxAnalysisInputsContainer;
